import { useMemo } from 'react';
import {
    GrowthBook,
    type GrowthBookSSRData,
    useFeatureIsOn,
    useFeatureValue,
    useGrowthBook,
} from '@growthbook/growthbook-react';
import type { Tracker } from '@archipro-website/tracker';
import { useRootData } from './use-root-data';

/**
 * Define all our GrowthBook feature flags and types here.
 */
export interface GrowthBookAppFeatures {
    'logged-in-share-information-downloads': boolean;
    'guest-homepage': boolean;
    product_tagging_dots: boolean;
    'site-gating': boolean;
    'article-gating': boolean;
    'design-board-gating': boolean;
    'directory-tile-slider': boolean;
    'directory-tile-downloads': boolean;
    project_tile_stats: boolean;
    'directory-potms': boolean;
    'default-filter-open': boolean;
    'tile-media-slider': boolean; // BC-2247: Won
    'analytics-amplifier-tiles': boolean;
    'tile-slider-disabled': boolean; // BC-3372
}

/**
 * Get the GrowthBook object for the website.
 * @param ssrConfig The attributes and features.
 * @param tracker The tracker object.
 * @returns The GrowthBook object.
 */
export function useGrowthBookClient(
    ssrConfig: GrowthBookSSRData,
    tracker: Tracker,
): GrowthBook<GrowthBookAppFeatures> {
    const {
        env: { stage },
        localisation: { country },
    } = useRootData();

    return useMemo(
        () =>
            new GrowthBook({
                apiHost: 'https://cdn.growthbook.io',
                enableDevMode: stage !== 'Production',
                attributes: ssrConfig.attributes,
                features: ssrConfig.features,
                trackingCallback: (experiment, result) => {
                    if (typeof window !== 'undefined') {
                        tracker.log('ExperimentViewed', {
                            url: new URL(window.location.href),
                            targetTracker: ['ga4Tracker', 'archiproTracker'],
                            data: {
                                ExtraData: JSON.stringify({
                                    experiment_id: experiment.key,
                                    variation_id: result.variationId,
                                }),
                            },
                            ga4Data: {
                                event_label: 'experiment_viewed',
                                event_category: 'experiment',
                                experiment_id: experiment.key,
                                variation_id: result.variationId,
                                country,
                            },
                        });
                    }
                },
            }),
        [stage, country, ssrConfig, tracker],
    );
}

// eslint-disable-next-line import/no-unused-modules
export function useABTest() {
    return useGrowthBook<GrowthBookAppFeatures>();
}

/**
 * Check if the feature flag is turned on in GrowthBook. Include the
 * professional ID and whitelist to turn off the feature flag for a specific
 * professional.
 * @param id The ID of the feature flag.
 * @param professionalID The ID of the professional.
 * @param whitelist The list of professional IDs to whitelist.
 * @returns Whether the feature flag is on or off.
 */
export function useABTestIsOn(
    id: keyof GrowthBookAppFeatures,
    professionalID: number | null = null,
    whitelist: number[] = [],
) {
    const isOn = useFeatureIsOn<GrowthBookAppFeatures>(id);
    if (professionalID && whitelist.includes(professionalID)) {
        return false;
    }
    return isOn;
}

/**
 * Get the value of the feature flag in GrowthBook.
 * @param id The ID of the feature flag.
 * @param fallback The fallback value. Defaults to 'control'.
 * @returns The value of the feature flag.
 */
export function useABTestValue(
    id: keyof GrowthBookAppFeatures,
    fallback: string = 'control',
) {
    return useFeatureValue(id, fallback);
}
